import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Radio, Wifi, WifiOff, Copy, Check, ExternalLink, Zap, TestTube, ChevronDown, ChevronUp, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WebhookStatus({ refreshKey }) {
  const [status, setStatus] = useState(null);
  const [webhookUrls, setWebhookUrls] = useState(null);
  const [customUrl, setCustomUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [sigVerification, setSigVerification] = useState(null);
  const [activating, setActivating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStatus();
    fetchWebhookUrl();
    fetchSecurityStatus();
  }, [refreshKey]);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/ghl/webhook/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* ignore */ }
  };

  const fetchWebhookUrl = async () => {
    try {
      const { data } = await axios.get(`${API}/api/ghl/webhook/url`, { withCredentials: true });
      setWebhookUrls(data);
      if (data.custom_url) setCustomUrl(data.custom_url);
    } catch { /* ignore */ }
  };

  const fetchSecurityStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/ghl/webhook/security`, { withCredentials: true });
      setSigVerification(data.signature_verification_enabled);
    } catch { /* ignore */ }
  };

  const copyUrl = async () => {
    const url = webhookUrls?.active_url || status?.webhook_url || '';
    if (url) {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const saveCustomUrl = async () => {
    if (!customUrl.trim()) return;
    setError('');
    try {
      await axios.post(`${API}/api/ghl/webhook/url`, { webhook_url: customUrl }, { withCredentials: true });
      await fetchWebhookUrl();
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
  };

  const activateWebhook = async () => {
    setActivating(true);
    setError('');
    try {
      await axios.post(`${API}/api/ghl/webhook/activate`, {}, { withCredentials: true });
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate');
    } finally {
      setActivating(false);
    }
  };

  const deactivateWebhook = async () => {
    setActivating(true);
    try {
      await axios.post(`${API}/api/ghl/webhook/deactivate`, {}, { withCredentials: true });
      await fetchStatus();
    } catch { /* ignore */ }
    finally { setActivating(false); }
  };

  const testWebhook = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/ghl/webhook/test`, {}, { withCredentials: true });
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const activeUrl = webhookUrls?.active_url || status?.webhook_url || '';

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-5 transition-all duration-200 hover:border-[#7B2FFF]/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-[#7B2FFF]" />
          <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Webhook Setup
          </h3>
        </div>
        {status?.active && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#7B2FFF] pulse-glow" />
            <span className="text-xs text-[#7B2FFF] font-mono">LIVE</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Webhook URL display */}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Your Webhook URL</Label>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md bg-[#111111] border border-[#27272A] min-w-0">
              <code className="text-sm text-[#EDEDF0] font-mono truncate flex-1" data-testid="webhook-url-display">
                {activeUrl || 'Save GHL settings first'}
              </code>
            </div>
            <Button
              data-testid="copy-webhook-url"
              onClick={copyUrl}
              disabled={!activeUrl}
              variant="outline"
              size="sm"
              className="border-[#27272A] bg-transparent text-[#A1A1AA] hover:text-[#EDEDF0] hover:bg-[#ffffff08] hover:border-[#7B2FFF]/40 shrink-0 h-9 px-3"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          {sigVerification && (
            <div className="flex items-center gap-1.5 mt-1.5" data-testid="webhook-sig-verification">
              <Lock className="w-3 h-3 text-emerald-400" />
              <span className="text-xs text-emerald-400">Signature verification enabled</span>
            </div>
          )}
        </div>

        {/* Custom webhook URL override */}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#71717A]">Custom URL (optional — if your app is deployed elsewhere)</Label>
          <div className="flex gap-2">
            <Input
              data-testid="custom-webhook-url-input"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://your-domain.com/api/webhook/ghl"
              className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] text-sm rounded-md h-9 font-mono"
            />
            <Button
              data-testid="save-custom-url"
              onClick={saveCustomUrl}
              disabled={!customUrl.trim()}
              size="sm"
              className="bg-[#27272A] hover:bg-[#3f3f46] text-[#EDEDF0] h-9 px-3 shrink-0"
            >
              Save
            </Button>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-3 p-3 rounded-md bg-[#111111] border border-[#27272A]">
          {status?.active ? (
            <Wifi className="w-5 h-5 text-emerald-400" />
          ) : (
            <WifiOff className="w-5 h-5 text-[#71717A]" />
          )}
          <div className="flex-1">
            <p className="text-sm text-[#EDEDF0]">
              {status?.active ? 'Webhook Active' : 'Webhook Inactive'}
            </p>
            <p className="text-xs text-[#71717A] mt-0.5">
              {status?.active
                ? 'Ready to receive messages from your CRM'
                : 'Copy the URL above and paste it in your webhook settings'}
            </p>
          </div>
          <Badge
            className={`text-xs ${
              status?.active
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-[#27272A] text-[#71717A] border-[#27272A]'
            }`}
            data-testid="webhook-status-badge"
          >
            {status?.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        {/* Test result */}
        {testResult && (
          <div className={`p-3 rounded-md border text-xs font-mono ${
            testResult.success
              ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400'
              : 'bg-red-500/5 border-red-500/20 text-red-400'
          }`} data-testid="webhook-test-result">
            {testResult.success
              ? 'Webhook test successful! Your endpoint is reachable.'
              : `Test failed: ${testResult.error || `Status ${testResult.status_code}`}`}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {status?.active ? (
            <Button
              data-testid="deactivate-webhook-button"
              onClick={deactivateWebhook}
              disabled={activating}
              variant="outline"
              className="flex-1 border-[#27272A] bg-transparent text-[#A1A1AA] hover:text-red-400 hover:bg-red-400/5 hover:border-red-400/30 rounded-md h-9 text-sm"
            >
              Deactivate
            </Button>
          ) : (
            <Button
              data-testid="activate-webhook-button"
              onClick={activateWebhook}
              disabled={activating || !activeUrl}
              className="flex-1 bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-9 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98] disabled:opacity-40"
            >
              {activating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Mark as Active</span>
              )}
            </Button>
          )}
          <Button
            data-testid="test-webhook-button"
            onClick={testWebhook}
            disabled={testing || !activeUrl}
            variant="outline"
            className="border-[#27272A] bg-transparent text-[#A1A1AA] hover:text-[#EDEDF0] hover:bg-[#ffffff08] hover:border-[#7B2FFF]/40 rounded-md h-9 text-sm px-4"
          >
            {testing ? (
              <div className="w-4 h-4 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="flex items-center gap-1.5"><TestTube className="w-3.5 h-3.5" /> Test</span>
            )}
          </Button>
        </div>

        {/* Setup instructions toggle */}
        <button
          data-testid="toggle-webhook-instructions"
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex items-center gap-1.5 text-xs text-[#7B2FFF] hover:text-[#9353FF] transition-colors w-full"
        >
          {showInstructions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          How to set up the webhook in your CRM
        </button>

        {showInstructions && (
          <div className="p-4 rounded-md bg-[#111111] border border-[#27272A] space-y-3 text-xs text-[#A1A1AA]" data-testid="webhook-instructions">
            <p className="font-semibold text-[#EDEDF0] text-sm">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-2 leading-relaxed">
              <li>Copy the <strong className="text-[#EDEDF0]">Webhook URL</strong> above</li>
              <li>Go to your GoHighLevel account</li>
              <li>
                Navigate to <strong className="text-[#EDEDF0]">Settings &rarr; Business Profile &rarr; Integrations</strong>,
                or set up a <strong className="text-[#EDEDF0]">Workflow</strong> with an <em>Inbound Webhook</em> trigger
              </li>
              <li>Paste the webhook URL in the webhook/endpoint field</li>
              <li>Select events: <strong className="text-[#EDEDF0]">InboundMessage</strong> (SMS and Chat)</li>
              <li>Save in GHL, then come back here and click <strong className="text-[#EDEDF0]">"Mark as Active"</strong></li>
            </ol>
            <a
              href="https://help.gohighlevel.com/support/solutions/articles/48001238167-guide-to-custom-webhook-workflow-action"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#7B2FFF] hover:text-[#9353FF] mt-1"
            >
              <ExternalLink className="w-3 h-3" />
              GHL Webhook Documentation
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
